import { useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/hooks/useNotifications';
import { useApplicationListSync } from '@/hooks/useApplicationListSync';
import {
  hasListPatchPayload,
  notificationSupportsListPatch,
  patchApplicationFromNotification,
} from '@/lib/applicationNotificationPatch';
import {
  addNotificationWatchRef,
  isApplicationInWatchSet,
} from '@/services/applications/applicationWatchSet';
import type { Notification } from '@/types';

const LIST_RELEVANT_TYPES = new Set<Notification['type']>([
  'new_application',
  'screening_completed',
  'candidate_hired',
  'candidate_rejected',
  'candidate_withdrawn',
  'offer_declined',
  'pipeline_error',
]);

function shouldUpsertFromNotification(n: Notification, companyId: string | undefined): boolean {
  if (!companyId || n.companyId !== companyId) return false;
  if (!LIST_RELEVANT_TYPES.has(n.type)) return false;
  return Boolean(n.payload.positionId && n.payload.applicationId);
}

/**
 * Incremental list sync: notifications wake the Watch Set and patch list cache
 * when enriched payload is available (B2); otherwise fetch one application (GET).
 */
export function useApplicationListNotificationSync(): void {
  const { selectedCompany } = useAuth();
  const companyId = selectedCompany?.id;
  const { notifications } = useNotifications();
  const listSync = useApplicationListSync();
  const handledIdsRef = useRef(new Set<string>());

  useEffect(() => {
    if (!companyId) {
      handledIdsRef.current.clear();
      return;
    }

    for (const n of notifications) {
      if (handledIdsRef.current.has(n.id)) continue;
      if (!shouldUpsertFromNotification(n, companyId)) continue;

      handledIdsRef.current.add(n.id);
      const { positionId, applicationId } = n.payload;
      if (!positionId || !applicationId) continue;

      const wasAlreadyWatched = isApplicationInWatchSet(applicationId);
      addNotificationWatchRef(applicationId, positionId);

      if (n.type !== 'new_application' && wasAlreadyWatched) {
        continue;
      }

      if (
        notificationSupportsListPatch(n.type)
        && hasListPatchPayload(n.payload)
      ) {
        const patch = patchApplicationFromNotification(n, companyId);
        if (patch) {
          listSync.patchMany(companyId, [patch], patch.archived === true);
          continue;
        }
      }

      void listSync.upsertOne(companyId, positionId, applicationId).catch(() => {
        handledIdsRef.current.delete(n.id);
      });
    }
  }, [companyId, notifications, listSync]);
}
